import { useEffect, useState } from "react";
import { ApiClientError, fetchReviewCampaigns } from "../../api/client";
import type { ReviewCampaignsResponse } from "../../types/reviews";
import { StateBanner } from "../../components/StateBanner";

type Loadable<T> = { status: "loading" } | { status: "error"; message: string } | { status: "ok"; data: T };

const STORE_LABEL: Record<string, string> = { shopify: "Shopify", rakuten: "楽天市場", amazon: "Amazon" };
const STORE_COLOR: Record<string, string> = { shopify: "var(--rv-shopify)", rakuten: "var(--rv-rakuten)", amazon: "var(--rv-amazon)" };
const STATE_LABEL: Record<string, { label: string; cls: string }> = {
  live: { label: "配信中", cls: "rv-tag--good" },
  test: { label: "テスト中", cls: "rv-tag--warn" },
  off: { label: "停止中", cls: "rv-tag--muted" },
};

export function CampaignsTab({ month }: { month: string }) {
  const [state, setState] = useState<Loadable<ReviewCampaignsResponse>>({ status: "loading" });

  const load = () => {
    setState({ status: "loading" });
    fetchReviewCampaigns(month)
      .then((data) => setState({ status: "ok", data }))
      .catch((e: unknown) => setState({ status: "error", message: e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。" }));
  };

  useEffect(load, [month]);

  if (state.status === "loading") return <StateBanner kind="loading" message="読み込み中" />;
  if (state.status === "error") return <StateBanner kind="error" message={state.message} onRetry={load} />;

  const { kpis, campaigns } = state.data;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="rv-kpi-grid">
        <div className="rv-kpi-card">
          <span className="rv-kpi-label">全体記入率</span>
          <span className="rv-kpi-value">{kpis.fillRate !== null ? `${kpis.fillRate}%` : "―"}</span>
        </div>
        <div className="rv-kpi-card">
          <span className="rv-kpi-label">依頼メール送信数</span>
          <span className="rv-kpi-value">{kpis.sent.toLocaleString("ja-JP")}</span>
          <span className="rv-kpi-delta muted">クリック率 {kpis.clickRate !== null ? `${kpis.clickRate}%` : "―"}</span>
        </div>
        <div className="rv-kpi-card">
          <span className="rv-kpi-label">依頼経由レビュー数</span>
          <span className="rv-kpi-value">{kpis.viaRequest}</span>
          <span className="rv-kpi-delta muted">全体の {kpis.viaRequestShare !== null ? `${kpis.viaRequestShare}%` : "―"}</span>
        </div>
      </div>

      <div className="rv-card">
        <div className="rv-card-head">
          <span className="rv-card-title">施策一覧</span>
          <a className="rv-btn rv-btn-primary" href="https://cs.nayu.site" target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
            ＋ 施策を追加
          </a>
        </div>
        <div className="rv-campaign-table">
          <div className="rv-campaign-head">
            <span>施策名</span>
            <span>店舗</span>
            <span>タイミング</span>
            <span>送信数</span>
            <span>記入率</span>
            <span>状態</span>
          </div>
          {campaigns.map((c) => {
            const rateCls = c.rate >= 9 ? "good" : c.rate < 5 ? "bad" : "";
            const st = STATE_LABEL[c.state];
            return (
              <div className="rv-campaign-row" key={c.id}>
                <div className="rv-campaign-name">
                  <span>{c.name}</span>
                  <span className="rv-campaign-desc">{c.desc}</span>
                </div>
                <span style={{ color: STORE_COLOR[c.store] }}>{STORE_LABEL[c.store]}</span>
                <span>{c.timing}</span>
                <span>{c.sent.toLocaleString("ja-JP")}</span>
                <span className={`rv-rate ${rateCls}`}>{c.rate}%</span>
                <span className={`rv-tag ${st.cls}`}>{st.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
